import { useEffect, useState } from 'react'

/** Speech rarely goes past this RMS; scaling to it lets normal talking fill most of the bar. */
const FULL_SCALE_RMS = 0.25
const SAMPLE_MS = 80

/**
 * How loud a frame of time-domain samples is, from 0 (silence) to 1. Samples are bytes centred on
 * 128, as `AnalyserNode.getByteTimeDomainData` writes them.
 */
export function levelFromSamples(samples: Uint8Array): number {
  if (samples.length === 0) return 0
  let sumSquares = 0
  for (let i = 0; i < samples.length; i++) {
    const v = (samples[i] - 128) / 128
    sumSquares += v * v
  }
  const rms = Math.sqrt(sumSquares / samples.length)
  return Math.min(1, rms / FULL_SCALE_RMS)
}

/**
 * The live input level (0–1) of the stream's microphone, for the level bar in the device check
 * (SC-438). Returns 0 when there is no stream, no audio track, or `enabled` is false.
 */
export function useMicLevel(stream: MediaStream | null, enabled: boolean): number {
  const [level, setLevel] = useState(0)

  useEffect(() => {
    if (!enabled || !stream || stream.getAudioTracks().length === 0) {
      setLevel(0)
      return
    }
    const AudioContextCtor =
      window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AudioContextCtor) return

    const context = new AudioContextCtor()
    const analyser = context.createAnalyser()
    analyser.fftSize = 1024
    const source = context.createMediaStreamSource(stream)
    source.connect(analyser)
    const samples = new Uint8Array(analyser.fftSize)

    const timer = setInterval(() => {
      analyser.getByteTimeDomainData(samples)
      setLevel(levelFromSamples(samples))
    }, SAMPLE_MS)

    return () => {
      clearInterval(timer)
      source.disconnect()
      if (context.state !== 'closed') void context.close()
      setLevel(0)
    }
  }, [stream, enabled])

  return level
}
